import omit from "lodash/omit";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";

import type { ActiveFilterBarProps } from "../misc/ActiveFilterBar";
import {
  type FilterDescriptor,
  type FilterOption,
  optionsDescriptor,
  searchDescriptor,
} from "../misc/activeFilters";
import type { Sale } from "../types";
import { formatDateRange, GTE, hasBounds, LTE } from "./dateRangePresets";

// The host buttons are one choice, not independent toggles. ToggleFilterButton
// merges its value into the filters and judges "selected" on the defined keys
// only, so each value names all three keys and sets the two it does not use to
// undefined: picking one clears the others, and clicking it again clears all.
export const HOST_KEYS = {
  "attendee_sales_ids@cs": undefined,
  "host_sales_id@eq": undefined,
  "host_sales_id@is": undefined,
};

// Independent toggles, each on its own key. Each gets its own descriptor below:
// one group over all seven would clear every content filter on one removal.
// Each asset filter means a file of that kind exists, whatever its state: a
// failed upload is still something to go and look at.
export const CONTENT_OPTIONS: FilterOption[] = [
  {
    label: "resources.zoom_meetings.filters.has_summary",
    value: { "summary_count@gt": 0 },
  },
  {
    label: "resources.zoom_meetings.filters.screen_recording",
    value: { "screen_state@not.is": "null" },
  },
  {
    label: "resources.zoom_meetings.filters.camera_video",
    value: { "camera_state@not.is": "null" },
  },
  {
    label: "resources.zoom_meetings.filters.audio",
    value: { "audio_state@not.is": "null" },
  },
  {
    label: "resources.zoom_meetings.filters.has_transcript",
    value: { "transcript_state@not.is": "null" },
  },
  {
    label: "resources.zoom_meetings.filters.has_tasks",
    value: { "task_count@gt": 0 },
  },
  {
    label: "resources.zoom_meetings.filters.with_content",
    value: { "has_content@eq": true },
  },
];

export const STATUS_OPTIONS: FilterOption[] = [
  {
    label: "resources.zoom_meeting_summaries.status.pending",
    value: { "status@eq": "pending" },
  },
  {
    label: "resources.zoom_meeting_summaries.status.done",
    value: { "status@eq": "done" },
  },
];

/** One chip for the date range, reading exactly what the date button reads. */
const dateRangeDescriptor = (
  translate: (key: string, options?: Record<string, unknown>) => string,
): FilterDescriptor => ({
  keys: [GTE, LTE],
  chips: (values) => {
    const bounds = {
      gte: values[GTE] as string | undefined,
      lte: values[LTE] as string | undefined,
    };
    if (!hasBounds(bounds)) return [];
    return [
      {
        id: "date",
        label: formatDateRange(bounds, translate),
        remove: (v) => omit(v, [GTE, LTE]),
      },
    ];
  },
});

/** The meeting list's active-filter chips, in sidebar order. */
export const useZoomMeetingFilterBar = (): ActiveFilterBarProps => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  // Same query as the sidebar, so React Query serves both from one fetch.
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
    filter: { "disabled@neq": true },
  });

  return {
    descriptors: [
      searchDescriptor(),
      optionsDescriptor(Object.keys(HOST_KEYS), [
        ...(identity
          ? [
              {
                label: "resources.zoom_meetings.filters.my_meetings",
                value: {
                  ...HOST_KEYS,
                  "attendee_sales_ids@cs": `{${identity.id}}`,
                },
              },
            ]
          : []),
        ...(sales ?? [])
          .filter((sale) => sale.id !== identity?.id)
          .map((sale) => ({
            label: `${sale.first_name} ${sale.last_name}`.trim(),
            value: { ...HOST_KEYS, "host_sales_id@eq": sale.id },
          })),
        {
          label: "resources.zoom_meetings.filters.no_host",
          value: { ...HOST_KEYS, "host_sales_id@is": "null" },
        },
      ]),
      dateRangeDescriptor(translate),
      ...CONTENT_OPTIONS.map((option) =>
        optionsDescriptor(Object.keys(option.value), [option]),
      ),
      optionsDescriptor(["status@eq"], STATUS_OPTIONS),
    ],
  };
};
